import useTransferRouteByLocationsAndCity from "./useTransferRouteByLocationsAndCity";
import { getLocationIdToSend } from '../utils/getLocationIdToSend';
import { useState, useEffect, useRef } from 'react';
import TransportLocation from "../types/TransportLocation";

interface UseTransferRouteParams {
    cityId?: number;
    locationFrom?: TransportLocation;
    locationTo?: TransportLocation;
    execute?: boolean;
}

/**
 * Generic hook to get transfer routes
 * Handles common logic for ARRIVAL and DEPARTURE
 * Returns the first element of the collection
 * If empty, retries by inverting the parameters (from ↔ to)
 * Only retries once
 */
export default function useTransferRoute(params: UseTransferRouteParams) {
    const { cityId, locationFrom, locationTo, execute = true } = params;
    const [isInverted, setIsInverted] = useState(false);
    const hasRetriedRef = useRef(false);

    // Get location IDs, considering parentLocation
    // Only after they have finished loading
    const fromId = getLocationIdToSend(locationFrom);
    const toId = getLocationIdToSend(locationTo);

    // If empty, invert the parameters
    const finalFromId = !isInverted ? fromId : toId;
    const finalToId = !isInverted ? toId : fromId;

    const shouldExecute = execute && !!finalFromId && !!finalToId && !!cityId;

    // Get the transfer route
    const { data: routeCollection, isLoading, isError, error } = useTransferRouteByLocationsAndCity(
        finalFromId || undefined, 
        finalToId || undefined, 
        cityId, 
        { execute: shouldExecute }
    );

    // Convert collection to single route (first element)
    // If empty and not inverted, retry with inverted parameters (only once)
    useEffect(() => {
        if (shouldExecute && routeCollection && Array.isArray(routeCollection)) {
            if (routeCollection.length === 0 && !isInverted && !hasRetriedRef.current) {
                hasRetriedRef.current = true;
                setIsInverted(true);
            }
        }
    }, [routeCollection, shouldExecute, isInverted]);

    // Reset isInverted when locations change
    useEffect(() => {
        setIsInverted(false);
        hasRetriedRef.current = false;
    }, [locationFrom, locationTo]);

    const route = Array.isArray(routeCollection) && routeCollection.length > 0
        ? routeCollection[0]
        : null;

    return {
        route,
        isLoading: isLoading,
        isError,
        error,
    };
}


